import React from "react"
import Helmet from "react-helmet"
import PropTypes from "prop-types"
import { graphql, useStaticQuery } from "gatsby"

const seoQuery = graphql`
  {
    allSite {
      edges {
        node {
          siteMetadata {
            author
            description
            social {
              github
              instagram
              twitter
            }
            siteUrl
            title
          }
        }
      }
    }
  }
`

function SEO({
  title,
  description,
  children,
  url,
  image,
  published,
  pathname,
  timeToRead,
  lang,
  meta,
}) {
  const results = useStaticQuery(seoQuery)
  const site = results.allSite.edges[0].node.siteMetadata
  const twitter = site.social.twitter || {}

  const fullURL = path => (path ? `${site.siteUrl}${path}` : site.siteUrl)

  const metaTags = [
    { charset: "utf-8" },
    {
      "http-equiv": "X-UA-Compatible",
      content: "IE=edge",
    },
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1",
    },
    {
      name: "theme-color",
      content: "#fff",
    },
    {
      rel: "canonical",
      href: fullURL(pathname),
    },
    { itemprop: "name", content: title || site.title },
    { itemprop: "description", content: description || site.description },
    { itemprop: "image", content: fullURL(image) },
    { name: "description", content: description || site.description },

    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:site", content: site.name },
    { name: "twitter:title", content: title || site.title },
    { name: "twitter:description", content: description || site.description },
    { name: "twitter:creator", content: twitter },
    {
      name: "twitter:image",
      content: fullURL(image),
    },

    { property: "og:title", content: title || site.title },
    { property: "og:url", content: url },
    { property: "og:image", content: fullURL(image) },
    { property: "og:description", content: description || site.description },
    { property: "og:site_name", content: site.name },
  ]

  if (published) {
    metaTags.push({ name: "article:published_time", content: published })
  }

  if (timeToRead) {
    metaTags.push({ name: "twitter:label1", value: "Reading time" })
    metaTags.push({ name: "twitter:data1", value: `${timeToRead} min read` })
  }

  return (
    <Helmet
      title={title}
      titleTemplate={`%s | ${site.title}`}
      htmlAttributes={{ lang }}
      meta={metaTags.concat(meta)}
    >
      <link
        href="https://fonts.googleapis.com/css?family=Merriweather:700,700i&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css?family=Sacramento&display=swap&subset=latin-ext"
        rel="stylesheet"
      />
      <link
        rel="stylesheet"
        href="https://indestructibletype.com/fonts/Jost.css"
        type="text/css"
        charset="utf-8"
      />
      <script
        async
        src="//cdn.embedly.com/widgets/platform.js"
        charset="UTF-8"
      ></script>
      {children}
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `ja`,
  meta: [],
  keywords: [],
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  image: PropTypes.string,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
}

export default SEO
